import React from 'react';
import { cn } from '../../utils/cn';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'glass';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', isLoading = false, children, disabled, ...props }, ref) => {
    const baseStyles = 'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#4a82c2]/50 disabled:opacity-50 disabled:pointer-events-none rounded-xl active:scale-[0.98]';

    const variants = {
      primary: 'bg-gradient-to-r from-[#4a82c2] via-[#356aa5] to-[#184166] hover:from-[#3b75b4] hover:to-[#14304d] text-white shadow-lg shadow-[#4a82c2]/25 border border-sky-400/30 font-semibold',
      secondary: 'bg-slate-900 dark:bg-slate-800 hover:bg-slate-800 dark:hover:bg-slate-700 text-slate-100 border border-slate-700/80 shadow-md',
      outline: 'border border-[#4a82c2]/40 dark:border-slate-700 hover:border-[#4a82c2] text-slate-200 hover:text-[#4a82c2] bg-slate-900/60 hover:bg-[#4a82c2]/10 font-semibold',
      ghost: 'text-slate-300 hover:text-white hover:bg-slate-800/60',
      glass: 'bg-[#131f33]/80 hover:bg-[#1a2942] text-white border border-[#4a82c2]/30 backdrop-blur-md shadow-xl',
    };

    const sizes = {
      sm: 'text-xs px-3 py-1.5 gap-1.5',
      md: 'text-sm px-4 py-2.5 gap-2',
      lg: 'text-base px-6 py-3.5 gap-2.5',
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : null}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
